import { personName, themeCommand } from "@nexxt/app-kit";
import { connectClaudePath, myTokensPath, preparedActionsPath } from "@nexxt/contracts/identity";
import { Button, DropdownMenu, useSidebarRail } from "@nexxt/ui";
import { useNavigate } from "@tanstack/react-router";
import { Bell, Bot, CircleUser, Ellipsis, Keyboard, KeySquare, ListChecks, LogOut, Moon } from "lucide-react";
import { m } from "../../paraglide/messages.js";
import { signOut, useMe } from "../features/auth/me";

// Who is signed in, and a menu with the theme, the shortcuts, the notifications they receive, their access tokens, how to
// connect Claude and the way out. In the wide sidebar the name stays in view next to the menu; in the top bar and the narrow sidebar (`compact`)
// the icon of the user opens the menu, which names the user on top.

export function UserMenu({ compact = false, onShortcuts }: { compact?: boolean; onShortcuts: () => void }) {
  const rail = useSidebarRail();
  const theme = themeCommand();
  const navigate = useNavigate();
  const menu = (
    <DropdownMenu
      align={rail ? "start" : "end"}
      trigger={
        <Button variant="ghost" size="icon" aria-label={m.user_menu()} className="text-muted-foreground">
          {compact ? <CircleUser aria-hidden /> : <Ellipsis aria-hidden />}
        </Button>
      }
      {...(compact && { header: <UserSummary /> })}
      items={[
        { id: "theme", label: theme.label, icon: <Moon aria-hidden />, onSelect: theme.run },
        { id: "shortcuts", label: m.shortcuts_title(), icon: <Keyboard aria-hidden />, onSelect: onShortcuts },
        {
          id: "notification-preferences",
          label: m.notification_preferences_title(),
          icon: <Bell aria-hidden />,
          onSelect: () => void navigate({ to: "/settings/notifications" }),
        },
        // ADR-262: the personal tokens of the user, for a script or an assistant
        {
          id: "access-tokens",
          label: m.delegated_access_mine_title(),
          icon: <KeySquare aria-hidden />,
          onSelect: () => void navigate({ to: myTokensPath }),
        },
        // ADR-263: how to connect Claude to Nexxt, with the address of the MCP server
        {
          id: "connect-claude",
          label: m.connect_claude_title(),
          icon: <Bot aria-hidden />,
          onSelect: () => void navigate({ to: connectClaudePath }),
        },
        // ADR-265: what Claude or a token prepared, for the user to confirm or discard
        {
          id: "prepared-actions",
          label: m.prepared_actions_title(),
          icon: <ListChecks aria-hidden />,
          onSelect: () => void navigate({ to: preparedActionsPath }),
        },
        { id: "sign-out", label: m.sign_out(), icon: <LogOut aria-hidden />, onSelect: () => void signOut() },
      ]}
    />
  );
  if (compact) return menu;
  return (
    <div className="flex items-center gap-2 pl-2">
      <div className="min-w-0 flex-1">
        <UserSummary />
      </div>
      {menu}
    </div>
  );
}

function UserSummary() {
  const me = useMe();
  return (
    <div className="min-w-0 text-xs">
      <p className="truncate font-medium">{personName(me)}</p>
      <p className="truncate text-muted-foreground">{me.organization.name}</p>
    </div>
  );
}
